import type { FC } from 'react'
import React, { useState, useMemo, useEffect, useCallback, memo } from 'react'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import Grid from '@material-ui/core/Grid'
import { useDispatch, useSelector } from 'react-redux'
import { isEqual, capitalizeText } from '@homeviews/utils'
import { Box } from '@material-ui/core'
import { mixpanelTrack } from '@homeviews/seo'

import {
  useGetMediaQuery,
  useAddMediaMutation,
  useUpdateMediaMutation,
  useDeleteMediaMutation,
  setActiveMedia,
  selectActiveMedia,
  setMediaFilter,
  selectActiveRes,
  selectActiveGuide,
  setResFilter,
  setActiveRes,
  useGetResourcesQuery,
  useGetGuidesQuery,
  setGuidesFilter,
  clearGuidesFilters,
  clearResFilters,
  clearMediaFilters,
  selectMediaFilters,
  selectResFilters,
  selectGuidesFilters,
  getUser,
} from '@homeviews/store'
import {
  Typography,
  Button,
  Card,
  Select,
  Pagination,
  Filters,
  SearchInput,
  PreviewDrawer,
  IconButton,
  Table,
  Snackbar,
  Dialog,
  TextField,
} from '@homeviews/shared-ui'
import type { UploadFile, FilterType } from '@homeviews/models'
import type { ITheme } from '@homeviews/styles'
import type { MediaItem } from '@homeviews/models'
import MediaCard from '../MediaCard'
import UploadZone from '../UploadZone'
import EditMediaForm from '../EditMediaForm'
import Selections from '../Selections'
import { headCells, createRows } from '../../helpers/tableResolvers'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {},
    modal: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'fixed',
      width: '100%',
      height: '100%',
      top: '50%',
      left: '50%',
      zIndex: 9999,
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      overflowY: 'auto',
      '& .c-mediaCore__inner': {
        maxHeight: 'calc(100vh - 48px)',
        maxWidth: 'calc(100vw - 48px)',
        width: '100%',
        height: '100%',
        position: 'relative',
        backgroundColor: theme.colors.white,
        display: 'flex',
        flexDirection: 'column',
      },
    },
    confirmationIcon: {
      transform: 'translateY(2px)',
      marginRight: 6,
    },
    inner: {},
    filterWrapper: {
      marginRight: theme.spacing(0.75),
      marginBottom: theme.spacing(1.875 / 2),
      marginTop: theme.spacing(1.5 / 2),
      position: 'relative',
      zIndex: 5,
    },
    selectWrapper: {
      fontWeight: 500,
      width: 170,
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    paginationWrapper: {
      paddingTop: 10,
    },
    searchWrapper: {
      marginRight: 20,
      width: 160,
    },
    filterWrapperButton: {
      marginRight: 'auto',
    },
    filtersTitle: {},
    mainFilters: {
      display: 'flex',
      justifyItems: 'center',
    },
    viewButtons: {
      display: 'flex',
      marginLeft: 10,
    },
    toggleViewButton: {
      opacity: 0.5,
    },
    toggleViewButtonActive: {
      opacity: 1,
    },
    tableWrapper: {
      marginTop: 30,
    },
    header: {
      display: 'flex',
      marginTop: 20,
    },
    headerCard: {
      flex: 1,
    },
    itemsWrapper: {
      overflowX: 'hidden',
      heighT: '100%',
      maxHeight: 'calc(100vh - 380px)',
      minHeight: 500,
      flex: 1,
    },
    mainCard: {
      flex: 1,
    },
  }),
)

type TypeOption = 'archive' | 'photo' | 'content' | 'other'
interface IProps {
  libraryType: 'generic' | 'images' | 'guides' | 'resources'
  typeOptions?: TypeOption[]
  header: string
  formats: string
  selectable?: boolean
  selectLimit?: number
  handleUseSelected?: (items: any[]) => void
  onClose?: () => void
  openInWindow?: boolean
  selected?: any[]
  defaultView?: 'cards' | 'table'
  disabledCardsView?: boolean
  disabledTableView?: boolean
}

const ACTIONS = {
  images: {
    setFilter: setMediaFilter,
    setActiveItem: setActiveMedia,
    clearAll: clearMediaFilters,
    selectActiveItem: selectActiveMedia,
    selectActiveFilters: selectMediaFilters,
  },
  guides: {
    setFilter: setGuidesFilter,
    setActiveItem: () => {
      //
    },
    clearAll: clearGuidesFilters,
    selectActiveItem: selectActiveGuide,
    selectActiveFilters: selectGuidesFilters,
  },
  resources: {
    setFilter: setResFilter,
    setActiveItem: setActiveRes,
    clearAll: clearResFilters,
    selectActiveItem: selectActiveRes,
    selectActiveFilters: selectResFilters,
  },
}

interface IMediaToDelete {
  id?: number
  confirmationText?: string
}

const MediaCore: FC<IProps> = memo(
  ({
    libraryType,
    typeOptions = ['archive', 'photo', 'content', 'other'],
    header,
    formats,
    selectable,
    handleUseSelected,
    openInWindow = false,
    selected = [],
    onClose,
    selectLimit,
    defaultView = 'cards',
    disabledCardsView,
    disabledTableView,
  }) => {
    const ALLOWED_TYPES = formats?.split(',')?.map?.((item) => item.trim()) || [
      'PNG',
      'JPG',
      'GIF',
      'SVG',
    ]
    const dispatch = useDispatch()
    const user = useSelector(getUser)
    const [selectedMediaItems, setSelectedMediaItems] = useState([])
    const [snackbarVisible, setSnackbarVisible] = useState<{
      open: boolean
      message: string
      status?: 'success' | 'error'
    }>({
      open: false,
      message: '',
      status: undefined,
    })

    const [mediaToDelete, setMediaToDelete] = useState<IMediaToDelete>({
      id: null,
      confirmationText: '',
    })

    const activeData: any = useSelector(
      ACTIONS[libraryType]['selectActiveItem'],
    )
    const activeFilters = useSelector(
      ACTIONS[libraryType]['selectActiveFilters'],
    )
    const classes = useStyles()

    const [listView, setListView] = useState<'cards' | 'table'>(defaultView)
    const [loadMediaOnPageChange, setLoadMediaOnPageChange] = useState<
      'none' | 'first' | 'last'
    >('none')

    const { data: mediaData } = useGetMediaQuery(
      {
        developerId: user?.developer_id,
        payload: activeFilters,
      },
      {
        skip: !user?.developer_id || libraryType !== 'images',
      },
    )

    const { data: resourcesData } = useGetResourcesQuery(
      {
        developerId: user?.developer_id,
        payload: activeFilters,
      },
      {
        skip: !user?.developer_id || libraryType !== 'resources',
      },
    )

    const { data: guidesData } = useGetGuidesQuery(
      {
        developerId: user?.developer_id,
        payload: activeFilters,
      },
      {
        skip: !user?.developer_id || libraryType !== 'guides',
      },
    )

    const [addMedia, { isLoading: isAddingMedia, error: isAddingMediaError }] =
      useAddMediaMutation()
    const [
      updateMedia,
      { isLoading: isUpdatingMedia, error: isUpdatingMediaError },
    ] = useUpdateMediaMutation()
    const [deleteMedia, { isLoading: isDeletingMedia }] =
      useDeleteMediaMutation()

    useEffect(() => {
      if (selected?.length && mediaData?.data?.length) {
        setSelectedMediaItems(selected)
      }
    }, [mediaData, selected])

    let allData = []
    let meta: any = {}

    switch (libraryType) {
      case 'resources':
        allData = resourcesData?.data
        meta = resourcesData
        break
      case 'images':
      case 'generic':
        allData = mediaData?.data
        meta = mediaData
        break
      case 'guides':
        allData = guidesData?.data
        meta = guidesData
        break
      default:
        allData = []
    }

    useEffect(() => {
      if (!activeData) {
        return
      }
      let newActiveData = null
      if (loadMediaOnPageChange === 'first') {
        newActiveData = allData[0]
      } else if (loadMediaOnPageChange === 'last') {
        newActiveData = allData[allData.length - 1]
      }
      newActiveData &&
        libraryType === 'images' &&
        dispatch(
          ACTIONS[libraryType]['setActiveItem']({
            media: newActiveData,
          }),
        )
    }, [meta?.current_page])

    const handleAddMedia = async (payload: UploadFile) => {
      try {
        const response: any = await addMedia({
          developerId: user?.developer_id,
          payload,
        })

        if (response?.error?.status) {
          handleResponseError()
        } else {
          setSnackbarVisible({
            open: true,
            message: 'Media has been uploaded',
          })
        }
      } catch (err) {
        handleResponseError()
      }
    }

    const onDeleteMediaButtonClick = (id: number) => {
      setMediaToDelete({
        ...mediaToDelete,
        id,
      })
    }

    const handleDeleteMedia = async () => {
      try {
        setMediaToDelete({
          id: null,
          confirmationText: '',
        })

        const response: any = await deleteMedia({
          developerId: user?.developer_id,
          media: mediaToDelete?.id,
        })

        if (response?.error?.status) {
          handleResponseError()
        } else {
          setSnackbarVisible({
            open: true,
            message: 'Media has been deleted',
          })
          dispatch(
            ACTIONS[libraryType]['setActiveItem']({
              media: null,
            }),
          )
        }
      } catch (err) {
        console.log('err', err)
        handleResponseError()
      }
    }

    const handleUpdateMedia = async (payload: UploadFile) => {
      try {
        const response: any = await updateMedia({
          developerId: user?.developer_id,
          data: payload,
          media: activeData.id,
        })
        if (response?.error?.status) {
          handleResponseError()
        } else {
          setSnackbarVisible({
            open: true,
            message: 'Media has been updated',
          })
        }
      } catch (err) {
        handleResponseError()
      }
    }

    function handleResponseError() {
      setSnackbarVisible({
        open: true,
        message: 'Oops, something went wrong',
        status: 'error',
      })
    }

    const handleSnackbarClose = (
      event: React.SyntheticEvent | React.MouseEvent,
      reason?: string,
    ) => {
      if (reason === 'clickaway') {
        return
      }

      setSnackbarVisible({
        open: false,
        message: snackbarVisible?.message,
        status: snackbarVisible?.status,
      })
    }

    const handleSelect = (item: MediaItem) => {
      setSelectedMediaItems((prevSelected: MediaItem[]) => {
        let newSelected = [...prevSelected]

        if (
          newSelected.find(
            (selectedItem: MediaItem) => selectedItem.id === item.id,
          )
        ) {
          newSelected = newSelected.filter(
            (selectedItem: MediaItem) => selectedItem.id !== item.id,
          )
        } else {
          newSelected = [...newSelected, item]
        }

        if (selectLimit) {
          newSelected = newSelected.slice(-selectLimit)
        }

        return newSelected
      })
    }

    const handleDataItemClick = useCallback(
      (id: number) => {
        const newData = allData.find((item: MediaItem) => item.id === id)

        if (selectable) {
          handleSelect(newData)
          return
        }

        if (newData && libraryType === 'images') {
          dispatch(
            ACTIONS[libraryType]['setActiveItem']({
              media: newData,
            }),
          )
          return
        }

        if (newData.url) {
          window.open(newData.url, '_blank')
        }
      },
      [allData, libraryType, dispatch],
    )

    const handleClearFilters = () => {
      dispatch(ACTIONS[libraryType]['clearAll']())

      let eventKey = ''

      if (libraryType === 'guides') {
        eventKey = 'GUIDES_RESET_FILTERS'
      } else if (libraryType === 'resources') {
        eventKey = 'RESOURCES_RESET_FILTERS'
      }

      if (eventKey) {
        mixpanelTrack(eventKey)
      }
    }

    const handleDrawerClose = () => {
      setLoadMediaOnPageChange('none')
      dispatch(
        ACTIONS[libraryType]['setActiveItem']({
          media: null,
        }),
      )
    }

    const handlePageChange = (page: number) => {
      dispatch(
        ACTIONS[libraryType]['setFilter']({
          filter: 'page',
          value: page,
        }),
      )
    }

    const handlePerPageChange = (page: number) => {
      dispatch(
        ACTIONS[libraryType]['setFilter']({
          filter: 'posts_per_page',
          value: page,
        }),
      )
    }

    const handleSelectFilterChange = (filterType: FilterType) => (option) => {
      const value = option?.value || option?.value === 0 ? option.value : false

      dispatch(
        ACTIONS[libraryType]['setFilter']({
          filter: filterType,
          value,
        }),
      )

      let eventKey = ''
      let properties = {}

      if (libraryType === 'resources') {
        if (filterType === 'type') {
          eventKey = 'RESOURCES_TYPE_FILTER'
          properties = {
            Type: value,
          }
        } else if (filterType === 'format[]') {
          eventKey = 'RESOURCES_FORMAT_FILTER'
          properties = {
            Format: value,
          }
        }
      } else if (libraryType === 'guides') {
        if (filterType === 'type') {
          eventKey = 'GUIDES_TYPE_FILTER'
          properties = {
            Type: value,
          }
        } else if (filterType === 'format[]') {
          eventKey = 'GUIDES_FORMAT_FILTER'
          properties = {
            Format: value,
          }
        }
      }

      if (eventKey) {
        mixpanelTrack(eventKey, {
          ...properties,
        })
      }
    }

    const handleSearchChange = (value: any) => {
      dispatch(
        ACTIONS[libraryType]['setFilter']({
          filter: 'search',
          value,
        }),
      )

      let eventKey = ''

      if (libraryType === 'guides') {
        eventKey = 'GUIDES_SEARCH'
      } else if (libraryType === 'resources') {
        eventKey = 'RESOURCES_SEARCH'
      }

      if (eventKey) {
        mixpanelTrack(eventKey, {
          value,
        })
      }
    }

    const handleConfirmationTextChange = (
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const value = e.target.value
      setMediaToDelete({
        ...mediaToDelete,
        confirmationText: value,
      })
    }

    const validateDeleteConfirmation = (
      source: 'button' | 'input' = 'button',
    ): boolean => {
      if (source === 'input' && !mediaToDelete?.confirmationText) {
        return true
      }
      if (
        mediaToDelete?.confirmationText &&
        mediaToDelete.confirmationText.toLowerCase() === 'delete'
      ) {
        return true
      }
      return false
    }

    const renderFilters = () => (
      <>
        <div className={clsx(classes.filterWrapper, classes.searchWrapper)}>
          <SearchInput
            value={activeFilters?.['search'] || ''}
            onChange={handleSearchChange}
          />
        </div>
        <div className={classes.mainFilters} />
        <div className={clsx(classes.filterWrapper, classes.filtersTitle)}>
          Filters
        </div>
        <div className={clsx(classes.filterWrapper, classes.selectWrapper)}>
          <Select
            placeholder="Select format"
            options={ALLOWED_TYPES.map((type) => {
              return {
                value: type,
                label: type.toUpperCase(),
              }
            })}
            onChange={handleSelectFilterChange('format[]')}
            value={activeFilters?.['format[]']}
            isClearable
          />
        </div>
        {typeOptions?.length && (
          <div className={clsx(classes.filterWrapper, classes.selectWrapper)}>
            <Select
              placeholder="Select type"
              options={typeOptions.map((type) => {
                return {
                  value: type,
                  label: capitalizeText(type),
                }
              })}
              onChange={handleSelectFilterChange('type')}
              value={activeFilters?.['type']}
              isClearable
            />
          </div>
        )}
        <div
          className={clsx(classes.filterWrapper, classes.filterWrapperButton)}
        >
          <Button
            variant="outlined"
            customColor="dark"
            onClick={handleClearFilters}
          >
            Reset filters
          </Button>
        </div>
      </>
    )

    const switchMedia = (direction: 'prev' | 'next') => {
      const id = activeData?.id
      if (!id) return

      const activeReviewIndex = allData.findIndex(
        (media: MediaItem) => media.id === id,
      )

      const prev = allData[activeReviewIndex - 1]
      const next = allData[activeReviewIndex + 1]
      const current = allData[activeReviewIndex]

      let newMedia: MediaItem = null
      if (!direction) {
        newMedia = current
      } else if (direction === 'prev' && prev) {
        newMedia = prev
      } else if (direction === 'next' && next) {
        newMedia = next
      }

      if (!newMedia) {
        if (direction === 'prev') {
          setLoadMediaOnPageChange('last')
          // handlePageChange(meta.current_page - 1)
        } else if (direction === 'next') {
          setLoadMediaOnPageChange('first')
          // handlePageChange(meta.current_page + 1)
        }
        return
      }

      dispatch(
        ACTIONS[libraryType]['setActiveItem']({
          media: newMedia,
        }),
      )
    }

    let nextMediaDisabled = false
    let prevMediaDisabled = false

    if (!!allData?.length && activeData?.id) {
      const activeMediaIndex = allData.findIndex(
        (media: MediaItem) => media.id === activeData?.id,
      )
      if (activeMediaIndex || activeMediaIndex === 0) {
        if (meta?.current_page === 1 && activeMediaIndex === 0) {
          prevMediaDisabled = true
        }
        if (
          meta?.current_page === meta?.last_page &&
          activeMediaIndex === allData.length - 1
        ) {
          nextMediaDisabled = true
        }
      }
    }

    const handleTableEditClick = useCallback(
      (id: number) => {
        handleDataItemClick(id)
      },
      [handleDataItemClick],
    )

    const handleTableDeleteClick = useCallback(
      (id: number) => {
        handleDataItemClick(id)
      },
      [handleDataItemClick],
    )

    const triggerUseSelected = () => {
      handleUseSelected(selectedMediaItems)
    }

    const rows = useMemo(
      () =>
        listView === 'cards'
          ? []
          : createRows(
              allData,
              handleTableEditClick,
              handleTableDeleteClick,
              libraryType,
            ),
      [allData, listView, handleTableEditClick, handleTableDeleteClick],
    )

    return (
      <>
        <div className={clsx(classes.root, openInWindow && classes.modal)}>
          <div className={clsx(classes.inner, 'c-mediaCore__inner')}>
            {libraryType === 'images' ? (
              <div className={classes.header}>
                <Card className={classes.headerCard}>
                  <UploadZone
                    addMedia={handleAddMedia}
                    isAddingMedia={isAddingMedia}
                    error={isAddingMediaError}
                  />
                </Card>
                {openInWindow && (
                  <IconButton
                    svgIcon="close"
                    variant="transparent"
                    onClick={() => onClose()}
                  />
                )}
              </div>
            ) : null}
            <Card
              className={classes.mainCard}
              header={<Typography variant="h3">{header}</Typography>}
            >
              <Filters>
                {renderFilters()}
                <div className={classes.viewButtons}>
                  {!disabledCardsView && (
                    <IconButton
                      svgIcon="medium/grid"
                      svgSource="image"
                      variant="transparent"
                      onClick={() => setListView('cards')}
                      className={clsx(
                        classes.toggleViewButton,
                        listView === 'cards' && classes.toggleViewButtonActive,
                      )}
                    />
                  )}
                  {!disabledTableView && (
                    <IconButton
                      svgIcon="medium/list"
                      svgSource="image"
                      variant="transparent"
                      onClick={() => setListView('table')}
                      className={clsx(
                        classes.toggleViewButton,
                        listView === 'table' && classes.toggleViewButtonActive,
                      )}
                    />
                  )}
                </div>
              </Filters>
              <div className={classes.itemsWrapper}>
                <Grid container spacing={3}>
                  {listView === 'cards' &&
                    !!allData?.length &&
                    allData.map((mediaItem: any) => {
                      return (
                        <Grid
                          key={mediaItem.id}
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}
                          xl={2}
                        >
                          <MediaCard
                            mediaItem={mediaItem}
                            handleClick={handleDataItemClick}
                            selected={selectedMediaItems.find(
                              (item: MediaItem) => mediaItem.id === item.id,
                            )}
                          />
                        </Grid>
                      )
                    })}
                </Grid>
                {listView === 'table' && (
                  <div className={classes.tableWrapper}>
                    <Table headCells={headCells(libraryType)} rows={rows} />
                  </div>
                )}
              </div>
              {selectable && (
                <Selections
                  triggerUseSelected={triggerUseSelected}
                  selected={selectedMediaItems}
                  clearSelection={() => setSelectedMediaItems([])}
                />
              )}
              <div className={classes.paginationWrapper}>
                <Pagination
                  currentPage={meta?.current_page}
                  lastPage={meta?.last_page}
                  perPage={activeFilters?.['posts_per_page'] || 25}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                  withPerPageControls={false}
                />
              </div>
            </Card>
            <PreviewDrawer
              open={!!activeData}
              onClose={handleDrawerClose}
              currentPage={meta?.current_page}
              lastPage={meta?.last_page}
              navigate={switchMedia}
              prevDisabled={prevMediaDisabled}
              nextDisabled={nextMediaDisabled}
            >
              {!!activeData && libraryType === 'images' && (
                <EditMediaForm
                  {...activeData}
                  cancel={handleDrawerClose}
                  updateMedia={handleUpdateMedia}
                  isUpdatingMedia={isUpdatingMedia}
                  handleDeleteMedia={onDeleteMediaButtonClick}
                  error={isUpdatingMediaError}
                />
              )}
            </PreviewDrawer>
          </div>
        </div>
        <Snackbar
          open={snackbarVisible?.open}
          onClose={handleSnackbarClose}
          alertMessage={snackbarVisible?.message}
          status={snackbarVisible?.status}
        />
        <Dialog
          open={!!mediaToDelete?.id}
          dialogHeading={
            <Typography variant="h3">
              <img
                className={classes.confirmationIcon}
                src="assets/icons/medium/alert-red.svg"
                alt="Dialog icon"
              />
              Confirm deletion of user
            </Typography>
          }
          dialogContent={
            <div>
              <Box marginBottom={2}>
                <Typography />
              </Box>
              <TextField
                label="Type the word delete below and click confirm"
                value={mediaToDelete.confirmationText}
                onChange={handleConfirmationTextChange}
                error={!validateDeleteConfirmation('input')}
              />
            </div>
          }
          dialogActions={
            <div>
              <Button
                variant="outlined"
                customVariant="outlinedPrimary"
                onClick={() => {
                  setMediaToDelete({
                    id: null,
                    confirmationText: '',
                  })
                }}
              >
                Cancel without delete
              </Button>
              <Button
                variant="contained"
                customVariant="containedPrimary"
                onClick={() => {
                  void handleDeleteMedia()
                }}
                disabled={!validateDeleteConfirmation()}
                isLoading={isDeletingMedia}
              >
                Confirm
              </Button>
            </div>
          }
        />
      </>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
)

export default MediaCore
